<template>
  <div class="theme-cap container-fluid py-4">

    <!-- Header -->
    <div class="hero mb-5 d-flex align-items-center justify-content-between">
      <div>
        <h2 class="hero-title mb-1">{{ $t('dashboard.title') }}</h2>
        <p class="hero-sub">
          {{ $t('dashboard.subtitle', { time: lastSync }) }}
        </p>
      </div>

      <div class="hero-actions d-flex gap-2">
        <button class="btn btn-ghost" @click="fetchStats">
          <i class="fas fa-sync me-2"/> 
          {{ $t('dashboard.refresh') }}
        </button>

        <button class="btn btn-ghost" @click="exportPNG">
          <i class="fas fa-download me-2"/>
          {{ $t('dashboard.export') }}
        </button>
      </div>
    </div>

    <!-- KPI Cards -->
    <div class="row g-4 mb-4">
      <div class="col-xl-3 col-md-6" v-for="c in kpiCards" :key="c.key">
        <div class="kpi-card shadow-sm">
          <div class="kpi-icon" :class="c.accent">
            <i :class="c.icon"/>
          </div>
          <div class="kpi-meta">
            <div class="kpi-label">{{ $t(c.label) }}</div>
            <div class="kpi-value">{{ c.value }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts -->
    <div class="row g-4">
      <div class="col-lg-6">
        <div class="panel shadow-sm">
          <div class="panel-header">
            <span class="panel-title">
              <i class="fas fa-venus-mars me-2"></i>
              {{ $t('dashboard.charts.genderTitle') }}
            </span>
          </div>
          <div class="panel-body">
            <div class="chart-box"><canvas ref="genderCanvas"/></div>
            <div class="muted small mt-2">{{ $t('dashboard.charts.genderSubtitle') }}</div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="panel shadow-sm">
          <div class="panel-header alt">
            <span class="panel-title">
              <i class="fas fa-user-graduate me-2"></i>
              {{ $t('dashboard.charts.educationTitle') }}
            </span>
          </div>
          <div class="panel-body">
            <div class="chart-box"><canvas ref="eduCanvas"/></div>
            <div class="muted small mt-2">
              {{ $t('dashboard.charts.educationSubtitle', { time: lastSync }) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner">
        <i class="fas fa-circle-notch fa-spin"/> {{ $t('dashboard.loading') }}
      </div>
    </div>

  </div>
</template>

<script>
import axios from '@/api'
import Chart from 'chart.js/auto'

export default {
  name: 'DashboardPro',

  data() {
    return {
      loading: false,
      lastSync: '',
      kpis: {
        totalEmployees: 0,
        activeContracts: 0,
        expiringContracts: 0,
        noContract: 0
      },
      education: {
        labels: ['BTS', 'BTB', 'Licence', 'Master', 'PhD', 'None'],
        data: [0, 0, 0, 0, 0, 0]
      },
      genderCounts: { Male: 0, Female: 0 },
      genderChart: null,
      eduChart: null
    }
  },

  computed: {
    kpiCards() {
      return [
        { key: 'total', label: 'dashboard.kpi.total', value: this.kpis.totalEmployees, icon: 'fas fa-users', accent: 'ink' },
        { key: 'active', label: 'dashboard.kpi.active', value: this.kpis.activeContracts, icon: 'fas fa-clipboard-check', accent: 'brand' },
        { key: 'expiring', label: 'dashboard.kpi.expiring', value: this.kpis.expiringContracts, icon: 'fas fa-hourglass-half', accent: 'ink' },
        { key: 'none', label: 'dashboard.kpi.none', value: this.kpis.noContract, icon: 'fas fa-exclamation', accent: 'brand' }
      ]
    }
  },

  mounted() {
    this.fetchStats()
  },

  methods: {
    palette(alpha = 1) {
      return {
        brand: `rgba(141, 46, 56, ${alpha})`,
        ink:   `rgba(75, 85, 99, ${alpha})`
      }
    },

    async fetchStats() {
      try {
        this.loading = true
        const { data } = await axios.get('/dashboard/stats')

        this.kpis.totalEmployees = data.total_employees ?? 0
        this.kpis.activeContracts = data.active_contracts ?? 0
        this.kpis.expiringContracts = data.contracts_expiring_month ?? 0
        this.kpis.noContract = data.employees_without_contract ?? 0

        this.lastSync = new Date().toISOString().slice(0, 19).replace('T', ' ')

        // Education
        const edu = data.education_levels || {}
        const order = ['BTS', 'BTB', 'Licence', 'Master', 'PhD', 'None']
        this.education.data = order.map(k => Number(edu[k] || 0))
        this.education.labels = order

        // Gender
        const g = data.employees_per_gender || {}
        this.genderCounts.Male = Number(g.Male || 0)
        this.genderCounts.Female = Number(g.Female || 0)

        this.drawGenderDonut()
        this.drawEducationBars()
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    drawGenderDonut() {
      if (this.genderChart) this.genderChart.destroy()

      const ctx = this.$refs.genderCanvas?.getContext('2d')
      if (!ctx) return

      this.genderChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: [
            this.$t('dashboard.charts.male'),
            this.$t('dashboard.charts.female')
          ],
          datasets: [{
            data: [this.genderCounts.Male, this.genderCounts.Female],
            backgroundColor: [this.palette(0.9).ink, this.palette(0.95).brand],
            hoverBackgroundColor: [this.palette(1).ink, this.palette(1).brand],
            borderWidth: 0
          }]
        },
        options: { responsive: true, cutout: '65%' }
      })
    },

    drawEducationBars() {
      if (this.eduChart) this.eduChart.destroy()

      const ctx = this.$refs.eduCanvas?.getContext('2d')
      if (!ctx) return

      this.eduChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.education.labels,
          datasets: [{
            label: this.$t('dashboard.charts.employees'),
            data: this.education.data,
            backgroundColor: this.palette(0.85).ink,
            borderRadius: 10,
            maxBarThickness: 44
          }]
        },
        options: { responsive: true, maintainAspectRatio: false }
      })
    },

    exportPNG() {
      const link = document.createElement('a')
      if (this.$refs.genderCanvas) {
        link.download = 'gender_donut.png'
        link.href = this.$refs.genderCanvas.toDataURL('image/png')
        link.click()
      }
      if (this.$refs.eduCanvas) {
        link.download = 'education_bars.png'
        link.href = this.$refs.eduCanvas.toDataURL('image/png')
        link.click()
      }
    }
  }
}
</script>

<style scoped>
/* Ton CSS reste inchangé */
.theme-cap { --brand: #8d2e38; --ink: #4b5563; --ink-600: #374151; --ink-300: #9ca3af; }

.hero { background: linear-gradient(135deg, var(--ink) 0%, var(--brand) 100%);
        border-radius: 1rem; padding: .8rem 1rem; color: #fff; box-shadow: 0 10px 24px rgba(0,0,0,.06);}  
.hero-title { font-weight: 800; letter-spacing: .2px; font-size: clamp(1.25rem, 2vw, 1.6rem);} 
.hero-sub { opacity: .9; font-size: .95rem; }
.btn-ghost { background: rgba(255,255,255,.12); color: #fff; border: 1px solid rgba(255,255,255,.22); border-radius: .7rem; padding: .45rem .8rem; font-weight: 700; font-size: .9rem; }
.btn-ghost:hover { background: rgba(255,255,255,.2); }

.kpi-card { display: grid; grid-template-columns: 56px 1fr; gap: 14px; align-items: center; background: #fff; border-radius: 1rem; padding: 1rem; border: 1px solid #eef0f3; transition: transform .2s ease, box-shadow .2s ease; }
.kpi-card:hover { transform: translateY(-2px); box-shadow: 0 10px 22px rgba(0,0,0,.06); }
.kpi-icon { width: 56px; height: 56px; display: grid; place-items: center; border-radius: .9rem; color: #fff; font-size: 1.2rem; }
.kpi-icon.ink { background: var(--ink); }
.kpi-icon.brand { background: var(--brand); }
.kpi-label { color: var(--ink-300); font-weight: 700; text-transform: uppercase; font-size: .72rem; letter-spacing: .08em; }
.kpi-value { color: var(--ink-600); font-weight: 800; font-size: 1.6rem; }

.panel { background: #fff; border-radius: 1rem; overflow: hidden; border: 1px solid #eef0f3; }
.panel-header { background: var(--ink); color: #fff; padding: .85rem 1rem; font-weight: 800; letter-spacing: .3px; }
.panel-header.alt { background: var(--brand); }
.panel-title { display: inline-flex; align-items: center; }
.panel-body { padding: 1rem; }
.muted { color: #6b7280; }
.chart-box { height: 300px; }

.loading-overlay { position: fixed; inset: 0; background: rgba(255,255,255,.6); backdrop-filter: blur(2px); display: grid; place-items: center; z-index: 9; }
.spinner { padding: .9rem 1.2rem; background: #fff; border-radius: .8rem; border: 1px solid #eef0f3; color: var(--brand); font-weight: 700; }
</style>
